import React, { useState, useEffect, useContext } from "react";
import "./PlansSelection.css";
import api from "../../../../utils/api";
import LoadingIcon from "../../../../components/LoadingIcon";
import PlanCheckIcon from "../../../../assets/planCheckIcon.svg";
import PlanTooltipIcon from "../../../../assets/planTooltipIcon.svg";
import PlanCheckIconActive from "../../../../assets/planCheckIconActive.svg";
import PlanTooltipIconActive from "../../../../assets/planTooltipIconActive.svg";
import { AuthContext } from "../../../../providers/AuthProvider";
import CompanyProfileModal from "../../components/CompanyProfileModal/CompanyProfileModal";

const PlansSelection = ({
  onPriceSelect,
  compactMode = false,
  showCurrentPlan = true,
  loading: externalLoading,
  plans: externalPlans
}) => {
  const { user, updateUser } = useContext(AuthContext);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [availablePeriods, setAvailablePeriods] = useState([]);
  const [changingPlanId, setChangingPlanId] = useState(null);
  const [showCompanyProfileModal, setShowCompanyProfileModal] = useState(false);
  const [pendingSelection, setPendingSelection] = useState(null);

  useEffect(() => {
    if (externalPlans) {
      setPlans(externalPlans);
      const periods = getAvailablePeriods(externalPlans);
      setAvailablePeriods(periods);
      if (periods.length > 0) {
        // Select the longest period by default
        const longestPeriod = periods.reduce((longest, current) =>
          current.months > longest.months ? current : longest
        );
        setSelectedPeriod(longestPeriod);
      }
      setLoading(false);
    } else {
      fetchPlans();
    }
  }, [externalPlans]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await api.get("/companies/payment_plans");

      let plansList = [];
      if (response.data?.data) {
        if (Array.isArray(response.data.data)) {
          plansList = response.data.data.map(item => ({
            id: item.id,
            ...item.attributes
          }));
        } else {
          plansList = [response.data.data];
        }
      } else if (Array.isArray(response.data)) {
        plansList = response.data;
      } else {
        console.error("Unexpected API response structure:", response.data);
      }

      setPlans(plansList);

      const periods = getAvailablePeriods(plansList);
      setAvailablePeriods(periods);

      if (periods.length > 0) {
        // Select the longest period by default
        const longestPeriod = periods.reduce((longest, current) =>
          current.months > longest.months ? current : longest
        );
        setSelectedPeriod(longestPeriod);
      }

      setLoading(false);
    } catch (error) {
      console.error("Erro ao buscar planos:", error);
      setLoading(false);
    }
  };

  const getAvailablePeriods = (plansList) => {
    const periods = [];
    const periodMap = {
      'monthly': { key: 'monthly', label: '1 mês', months: 1 },
      'quarterly': { key: 'quarterly', label: '3 meses', months: 3 },
      'semiannual': { key: 'semiannual', label: '6 meses', months: 6 },
      'annual': { key: 'annual', label: '1 ano', months: 12 }
    };

    const foundPeriods = new Set();
    plansList.forEach(plan => {
      if (plan.prices && Array.isArray(plan.prices)) {
        plan.prices.forEach(price => {
          if (price.active && price.period) {
            foundPeriods.add(price.period);
          }
        });
      }
    });

    Object.keys(periodMap).forEach(key => {
      if (foundPeriods.has(key)) {
        periods.push(periodMap[key]);
      }
    });

    return periods;
  };

  const getPlansForPeriod = () => {
    if (!selectedPeriod) return [];

    return plans
      .filter(plan => plan.active && !plan.trial && !plan.admin)
      .map(plan => {
        const priceForPeriod = plan.prices?.find(
          price => price.period === selectedPeriod.key && price.active
        );

        if (!priceForPeriod) return null;

        const isCurrent = showCurrentPlan &&
          user?.subscription?.paymentPlan?.id === plan.id &&
          user?.subscription?.period === priceForPeriod.period;

        return {
          ...plan,
          selectedPrice: priceForPeriod,
          monthlyAmount: priceForPeriod.amount,
          billingCycle: priceForPeriod.period,
          isCurrentPlan: isCurrent,
          hasSavings: priceForPeriod.hasSavings || false,
          savingsPerMonth: priceForPeriod.savingsPerMonth || 0,
          totalSavings: priceForPeriod.totalSavings || 0,
          originalPrice: priceForPeriod.originalPrice || 0
        };
      })
      .filter(plan => plan !== null)
      .sort((a, b) => a.monthlyAmount - b.monthlyAmount);
  };

  const filteredPlans = getPlansForPeriod();

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatBillingCycle = (billingCycle) => {
    if (!billingCycle) return "";
    const recurrenceMap = {
      monthly: "1 mês",
      quarterly: "3 meses",
      semiannual: "6 meses",
      annual: "12 meses"
    };
    return recurrenceMap[billingCycle];
  };

  const handlePlanClick = (plan) => {
    if (plan.isCurrentPlan || changingPlanId) return;

    if (!user?.readyForBilling) {
      setPendingSelection(plan);
      setShowCompanyProfileModal(true);
      return;
    }

    setChangingPlanId(plan.id);

    if (onPriceSelect && plan.selectedPrice) {
      onPriceSelect(plan.selectedPrice.id);
    }

    setTimeout(() => {
      setChangingPlanId(null);
    }, 1000);
  };

  const handleCompanyProfileModalSuccess = async () => {
    setShowCompanyProfileModal(false);

    try {
      const response = await api.get(`/companies/companies/${user.id}`);

      if (response?.data && updateUser) {
        await updateUser(response.data);
      }

      if (pendingSelection && onPriceSelect && pendingSelection.selectedPrice) {
        setChangingPlanId(pendingSelection.id);
        onPriceSelect(pendingSelection.selectedPrice.id);

        setTimeout(() => {
          setChangingPlanId(null);
        }, 1000);
      }

      setPendingSelection(null);
    } catch (error) {
      console.error("Error fetching updated company data:", error);
      setPendingSelection(null);
    }
  };

  const handleCompanyProfileModalCancel = () => {
    setShowCompanyProfileModal(false);
    setPendingSelection(null);
  };

  const isLoadingState = externalLoading !== undefined ? externalLoading : loading;

  return (
    <>
      <div className={`containerPlans ${compactMode ? 'compactMode' : ''}`} style={isLoadingState ? { justifyContent: 'center', alignItems: 'center' } : {}}>
      {isLoadingState ? (
        <div style={{ height: '150px' }}>
          <LoadingIcon />
        </div>
      ) : (
        <>
          {availablePeriods.length > 1 && (
            <div className="periodSelector">
              {availablePeriods.map(period => (
                <button
                  key={period.key}
                  className={`periodButton ${selectedPeriod?.key === period.key ? 'active' : ''}`}
                  onClick={() => setSelectedPeriod(period)}
                >
                  {period.label}
                </button>
              ))}
            </div>
          )}

          <div className="containerPlansList" style={{ marginTop: '15px' }}>
            {filteredPlans.map(plan => (
              <div
                key={plan.id}
                className={`planCard ${plan.isCurrentPlan ? 'currentPlan' : ''} ${plan.popular ? 'popularPlan' : ''}`}
                style={plan.popular ? { border: '2px solid #0AC6BD' } : {}}
              >
                {plan.popular && (
                  <div className="popularPlanBadge">
                    OFERTA POPULAR
                  </div>
                )}
                <h3 className="planName">{plan.name}</h3>
                <div className="planPriceContainer" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {plan.hasSavings && plan.totalSavings > 0 && (
                    <div className="savingsBadge">
                      Economia de R$ {formatCurrency(plan.totalSavings)}
                    </div>
                  )}

                  {plan.hasSavings && plan.totalSavings > 0 && (
                    <div className="originalPrice">
                      R$ {formatCurrency(plan.originalPrice)} / mês
                    </div>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', justifyContent: !plan.hasSavings ? 'center' : 'flex-start' }}>
                    {!plan.hasSavings && <div style={{ height: '28px', visibility: 'hidden' }}></div>}
                    <div className="planPrice">
                      <p className="descriptionPrice">R$</p>
                      <h1 className="amount">{formatCurrency(plan.monthlyAmount)}</h1>
                      <p className="descriptionPrice">/ mês</p>
                    </div>
                    {!plan.hasSavings && <div style={{ height: '22px', visibility: 'hidden' }}></div>}
                  </div>
                </div>

                <button
                  className={`planButton ${plan.isCurrentPlan ? 'currentButton' : 'changeButton'} ${changingPlanId === plan.id ? 'loading' : ''}`}
                  onClick={() => handlePlanClick(plan)}
                  disabled={plan.isCurrentPlan || changingPlanId === plan.id}
                  style={plan.popular ? { background: '#0AC6BD' } : {}}
                >
                  {changingPlanId === plan.id ? 'Processando...' : (plan.isCurrentPlan ? 'Seu plano atual' : 'Quero mudar para esse plano')}
                </button>

                {plan.benefits && plan.benefits.length > 0 && (
                  <ul className="planBenefits">
                    {plan.benefits.map((benefit, index) => {
                      const benefitText = typeof benefit === 'string' ? benefit : benefit.title;
                      const hasTooltip = benefit?.description;

                      return (
                        <li key={index} className="benefitItem">
                          <img
                            src={plan.isCurrentPlan ? PlanCheckIconActive : PlanCheckIcon}
                            alt="Check"
                            className="planCheckIcon"
                            width={16}
                            height={16}
                          />
                          <span className="benefitText">{benefitText}</span>
                          {hasTooltip && (
                            <div className="tooltipContainer">
                              <img
                                src={plan.isCurrentPlan ? PlanTooltipIconActive : PlanTooltipIcon}
                                alt="Info"
                                className="planTooltipIcon"
                                width={13}
                                height={13}
                              />
                              <div className="tooltipContent">
                                {benefit.description}
                              </div>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </>
      )}
      </div>

      {showCompanyProfileModal && user && (
        <CompanyProfileModal
          company={user}
          updateUser={updateUser}
          onSuccess={handleCompanyProfileModalSuccess}
          onCancel={handleCompanyProfileModalCancel}
        />
      )}
    </>
  );
};

export default PlansSelection;

